// The Life Study Invoice, one instalment invoice under the service agreement.
// Built from the shared documentKit for brand consistency with the other docs.
// The instalment amount is computed from the total fee divided into four equal
// instalments, so the figures are always internally consistent.

import {
  escapeHtml,
  fillField,
  buildMasthead,
  buildSignatureBlock,
  buildFooter,
  wrapDocument,
} from "./documentKit";

const COUNT = 4;
const fmt = (n) => n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
const money = (n) => `R ${fmt(n)}`;
const parseMoney = (s) => {
  const n = parseFloat(String(s || "").replace(/[^0-9.]/g, ""));
  return isNaN(n) ? 0 : n;
};

export const LS_INSTALMENTS = {
  "1": {
    label: "commencement instalment",
    desc: "First payment under the service agreement for a Life Study, the anthropological biography. Covers the commencement of work: the first conversations and the archival systematisation phase. Satisfied by conversion of the holding fee.",
  },
  "2": {
    label: "second-quarter instalment",
    desc: "Second payment under the service agreement for a Life Study. Due at the start of the second quarter, covering the participant-observed residency and the ongoing weekly conversations.",
  },
  "3": {
    label: "third-quarter instalment",
    desc: "Third payment under the service agreement for a Life Study. Due at the start of the third quarter, covering the social network triangulation, the dual reading of the photographic archive, and the composition of the work.",
  },
  "4": {
    label: "delivery instalment",
    desc: "Final payment under the service agreement for a Life Study. Due on delivery of the finished, printed heritage run.",
  },
};

const LS_INVOICE_CSS = `
.metablock{text-align:center;margin:-8px 0 6px;}
.metablock .meta{color:var(--slate);font-size:13px;margin:2px 0;}
.invtitle{text-align:center;font-style:italic;font-size:30px;letter-spacing:0.14em;margin:20px 0 4px;}
.invsub{text-align:center;font-style:italic;color:var(--slate);font-size:15px;margin-bottom:24px;}
.billing{display:flex;justify-content:space-between;gap:24px;margin-bottom:14px;font-size:13.5px;}
.billing .k{font-style:italic;color:var(--slate);}
.billing .right{text-align:right;}
.billing .colhead{font-variant:small-caps;letter-spacing:0.22em;color:var(--ox);font-size:11px;margin-bottom:5px;}
.linetable td .desc{color:var(--slate);font-style:italic;font-size:12px;display:block;margin-top:3px;}
.linetable td .title{color:var(--ox);}
.totes{width:100%;font-size:14px;margin-top:4px;}
.totes td{border:none;padding:5px 10px;}
.totes .lab{text-align:right;}
.totes .val{text-align:right;width:34%;}
.totes .grand .lab,.totes .grand .val{color:var(--ox);font-size:17px;padding-top:8px;}
.label{font-variant:small-caps;letter-spacing:0.28em;font-size:12px;color:var(--ox);margin:20px 0 6px;}
.pay p{font-size:13.5px;margin:3px 0;}
.notes p{font-size:13px;margin:6px 0;}
.sig .nm{font-style:italic;color:var(--ox);font-size:18px;}
.sched{width:100%;font-size:12.5px;margin:4px 0 6px;}
.sched .cur td{background:var(--deep);color:var(--ox);}
.sched .cur .mk{font-variant:small-caps;letter-spacing:0.16em;font-size:10px;}
.sched .paid td{color:var(--slate);}
.sched .st{font-style:italic;font-size:12px;}
.sched .cur .st{color:var(--ox);}
.totes.bal{margin:8px 0 4px;}
`;

export function buildLifeStudyInvoiceHtml(fields = {}) {
  const {
    billedTo,
    billedCity,
    invoiceNumber,
    invoiceDate,
    dueDate,
    projectTitle,
    agreementDate,
    instalmentNumber = "1",
    totalFee,
    description,
    commencementDate,
    q2Date,
    q3Date,
    deliveryDate,
    amountReceived,
  } = fields;

  const isFull = String(instalmentNumber) === "full";
  const n = ["1", "2", "3", "4"].includes(String(instalmentNumber))
    ? String(instalmentNumber)
    : isFull
    ? "full"
    : "1";
  const total = parseMoney(totalFee) || 1024000;
  const per = total / COUNT;
  const nNum = isFull ? 0 : Number(n);

  // This invoice bills the full fee (upfront) or a single instalment.
  const billed = isFull ? total : per;

  // Prior payments already received. If the admin has not overridden it, assume
  // the instalments before this one have been paid (0 for a full upfront invoice).
  const received =
    amountReceived && amountReceived.trim()
      ? parseMoney(amountReceived)
      : isFull
      ? 0
      : per * (nNum - 1);
  const outstandingAfter = Math.max(0, total - received - billed);

  const FULL_DESC =
    "Full payment of the Life Study fee, settled in advance at the client&#8217;s request. Covers the entire commission as set out in the service agreement: the conversations, the archive, the residency, the fieldwork, the composition, the reading months, and the heritage run.";
  const lineTitle = isFull
    ? "Full commission fee, paid upfront"
    : `Instalment ${n} of ${COUNT}, ${LS_INSTALMENTS[n].label}`;
  const desc =
    description && description.trim()
      ? escapeHtml(description.trim())
      : isFull
      ? FULL_DESC
      : LS_INSTALMENTS[n].desc;

  // Row status is driven by the amount actually received, so the schedule reads
  // as a true statement of account (supports partial and out-of-order payments).
  const statusFor = (kNum) => {
    if (received >= per * kNum - 0.01) return { cls: "paid", label: "Paid" };
    if (isFull || kNum === nNum) return { cls: "cur", label: "Due now" };
    return { cls: "", label: "Outstanding" };
  };
  const schedRows = [
    { k: "1", lab: "On commencement", date: commencementDate, dph: "commencement date" },
    { k: "2", lab: "At the second quarter", date: q2Date, dph: "date" },
    { k: "3", lab: "At the third quarter", date: q3Date, dph: "date" },
    { k: "4", lab: "On delivery", date: deliveryDate, dph: "date" },
  ]
    .map((r) => {
      const kNum = Number(r.k);
      const s = statusFor(kNum);
      const mark = !isFull && kNum === nNum ? ' <span class="mk">&#183; this invoice</span>' : "";
      return `<tr class="${s.cls}"><td>${r.k} of ${COUNT}, ${r.lab}${mark}</td><td>${fillField(
        r.date,
        r.dph
      )}</td><td class="st">${s.label}</td><td class="r">${fmt(per)}</td></tr>`;
    })
    .join("");

  const body = `${buildMasthead()}<div class="metablock"><div class="meta">The Raven&#8217;s Quill (Pty) Ltd &#183; Pretoria, South Africa</div><div class="meta">info@theravensquill.co.za &#183; +27 79 281 8435</div><div class="meta">Company registration no.: 2025/875945/07</div></div><div class="invtitle">I N V O I C E</div><div class="invsub">The Life Study</div><div class="billing"><div><div class="colhead">Billed to</div><div>${fillField(
    billedTo,
    "Subject&#x27;s full legal name"
  )}</div><div>${fillField(
    billedCity,
    "Subject&#x27;s city, province"
  )}</div></div><div class="right"><div><span class="k">Invoice number:</span> ${fillField(
    invoiceNumber,
    "RQ-2026-001"
  )}</div><div><span class="k">Invoice date:</span> ${fillField(
    invoiceDate,
    "date"
  )}</div><div><span class="k">Due date:</span> ${fillField(
    dueDate,
    "date"
  )}</div><div><span class="k">Project:</span> ${fillField(
    projectTitle,
    "project title"
  )}</div><div><span class="k">Agreement dated:</span> ${fillField(
    agreementDate,
    "date"
  )}</div></div></div><table class="linetable"><tr><th>Description</th><th class="r">Amount (ZAR)</th></tr><tr><td><span class="title">${lineTitle}</span><span class="desc">${desc}</span></td><td class="r strongcell">${fmt(
    billed
  )}</td></tr></table><table class="totes"><tr><td class="lab">Subtotal</td><td class="val">${money(
    billed
  )}</td></tr><tr><td class="lab">VAT</td><td class="val">Not applicable</td></tr><tr class="grand"><td class="lab">Total due</td><td class="val">${money(
    billed
  )}</td></tr></table>${
    isFull
      ? ""
      : `<div class="label">Instalment schedule</div><table class="sched"><tr><th>Instalment</th><th>Due</th><th>Status</th><th class="r">Amount (ZAR)</th></tr>${schedRows}</table>`
  }<table class="totes bal"><tr><td class="lab">Received to date</td><td class="val">${money(received)}</td></tr><tr><td class="lab">This invoice</td><td class="val">${money(billed)}</td></tr><tr class="grand"><td class="lab">Balance outstanding after this payment</td><td class="val">${money(outstandingAfter)}</td></tr></table><div class="label">Payment details</div><div class="pay"><p>Account name:&nbsp; The Raven&#8217;s Quill</p><p>Bank:&nbsp; First National Bank</p><p>Account number:&nbsp; 63182970137</p><p>Branch code:&nbsp; 250655</p><p>Reference:&nbsp; ${fillField(
    invoiceNumber,
    "RQ-2026-001"
  )}</p></div><div class="label">Notes</div><div class="notes"><p>${
    isFull
      ? `This invoice is for the full Life Study fee of ${money(
          total
        )}, settled in advance at the client&#8217;s request under the service agreement for ${fillField(
          projectTitle,
          "project title"
        )}, in place of the four quarterly instalments.`
      : `This is instalment ${n} of ${COUNT} under the service agreement for ${fillField(
          projectTitle,
          "project title"
        )}. The Life Study fee of ${money(
          total
        )} is settled in four equal instalments of ${money(
          per
        )}: the first held as a holding fee that converts into this commencement instalment, then at the start of the second quarter, the start of the third quarter, and on delivery.`
  } Travel for field residencies is invoiced separately against receipts and is not included above.</p><p>The Raven&#8217;s Quill (Pty) Ltd is not registered for VAT; no value-added tax is charged on this invoice. Payment by electronic funds transfer to the account above, using the reference provided.</p></div>${buildSignatureBlock()}${buildFooter({ fline: "THE RAVEN&#8217;S QUILL &#183; THE LIFE STUDY" })}`;

  return wrapDocument({ title: "The Life Study, Invoice", extraCss: LS_INVOICE_CSS, body });
}
